import {
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  TimePicker,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title } = Typography;
const { TextArea, Search } = Input;
const { Option } = Select;

interface Speaker {
  id: string;
  name: string;
  title?: string;
  company?: string;
}

interface Session {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  type: string;
  eventId: string;
  Event?: {
    id: string;
    title: string;
    Organization?: {
      name: string;
    };
  };
  SessionSpeaker?: {
    Speaker: Speaker;
  }[];
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
}

const sessionTypeConfig = {
  PRESENTATION: { color: 'blue', labelKey: 'sessions.types.presentation' },
  WORKSHOP: { color: 'green', labelKey: 'sessions.types.workshop' },
  PANEL: { color: 'purple', labelKey: 'sessions.types.panel' },
  NETWORKING: { color: 'orange', labelKey: 'sessions.types.networking' },
  BREAK: { color: 'default', labelKey: 'sessions.types.break' },
  KEYNOTE: { color: 'red', labelKey: 'sessions.types.keynote' },
} as const;

const Sessions: React.FC = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchEvents();
    fetchSessions();
  }, []);

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchSpeakers = async (eventId: string) => {
    try {
      console.log('Fetching speakers for event:', eventId);
      const response = await api.get('/speakers', { params: { eventId } });
      console.log('Speakers response:', response.data);
      setSpeakers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch speakers:', error);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedEvent) {
        params.eventId = selectedEvent;
      } else if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }

      const response = await api.get('/sessions', { params });
      setSessions(response.data || []);
    } catch (error) {
      message.error(t('sessions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [selectedEvent]);

  const handleCreate = () => {
    setEditingSession(null);
    form.resetFields();

    // Set default values
    const defaultValues: any = { type: 'PRESENTATION' };

    // If there's a selected event, use it as default
    if (selectedEvent) {
      defaultValues.eventId = selectedEvent;
      fetchSpeakers(selectedEvent);
    }

    form.setFieldsValue(defaultValues);
    setModalVisible(true);
  };

  const handleEdit = (record: Session) => {
    setEditingSession(record);
    const speakerIds = record.SessionSpeaker?.map((s) => s.Speaker.id) || [];
    form.setFieldsValue({
      ...record,
      datetime: [dayjs(record.startTime), dayjs(record.endTime)],
      speakerIds,
    });
    if (record.eventId) {
      fetchSpeakers(record.eventId);
    }
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/sessions/${id}`);
      message.success(t('sessions.deleted'));
      fetchSessions();
    } catch (error) {
      message.error(t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const [startTime, endTime] = values.datetime;
      const data = {
        ...values,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      };
      delete data.datetime;

      // Debug log to see what we're sending
      console.log('Submitting session data:', data);
      console.log('SpeakerIds:', data.speakerIds);

      if (editingSession) {
        await api.patch(`/sessions/${editingSession.id}`, data);
        message.success(t('sessions.updated'));
      } else {
        await api.post('/sessions', data);
        message.success(t('sessions.created'));
      }
      setModalVisible(false);
      setSpeakers([]); // Clear speakers after successful submit
      fetchSessions();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('sessions.operationFailed'));
    }
  };

  const handleEventChange = (eventId: string) => {
    console.log('Event changed to:', eventId);
    form.setFieldValue('eventId', eventId);
    if (eventId) {
      fetchSpeakers(eventId);
    } else {
      setSpeakers([]);
    }
  };

  // Filter sessions based on search text
  const filteredSessions = sessions.filter((session) => {
    const searchLower = searchText.toLowerCase();
    return (
      session.title.toLowerCase().includes(searchLower) ||
      session.description?.toLowerCase().includes(searchLower) ||
      session.location?.toLowerCase().includes(searchLower) ||
      session.type.toLowerCase().includes(searchLower) ||
      session.Event?.title?.toLowerCase().includes(searchLower) ||
      session.SessionSpeaker?.some(
        (ss) =>
          ss.Speaker.name.toLowerCase().includes(searchLower) ||
          ss.Speaker.company?.toLowerCase().includes(searchLower)
      )
    );
  });

  const columns: ColumnsType<Session> = [
    {
      title: t('common.time'),
      key: 'time',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <span>
            <CalendarOutlined /> {dayjs(record.startTime).format('YYYY-MM-DD')}
          </span>
          <span>
            <ClockCircleOutlined /> {dayjs(record.startTime).format('HH:mm')} -{' '}
            {dayjs(record.endTime).format('HH:mm')}
          </span>
        </Space>
      ),
      sorter: (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('sessions.speakers'),
      key: 'speakers',
      render: (_, record) => (
        <Space size="small" wrap>
          {record.SessionSpeaker && record.SessionSpeaker.length > 0 ? (
            record.SessionSpeaker.map((s) => (
              <Tag key={s.Speaker.id} icon={<UserOutlined />}>
                {s.Speaker.name}
              </Tag>
            ))
          ) : (
            <span>-</span>
          )}
        </Space>
      ),
    },
    {
      title: t('common.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: t('sessions.session_type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const config = sessionTypeConfig[type as keyof typeof sessionTypeConfig];
        return <Tag color={config.color}>{t(config.labelKey)}</Tag>;
      },
    },
    {
      title: t('sessions.location'),
      dataIndex: 'location',
      key: 'location',
      render: (location: string) =>
        location ? (
          <span>
            <EnvironmentOutlined /> {location}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: t('common.event'),
      dataIndex: ['Event', 'title'],
      key: 'event',
      render: (title: string) => title || '-',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={2} style={{ margin: 0 }}>
              {t('sessions.title')}
            </Title>
            <Badge
              count={filteredSessions.length}
              style={{ backgroundColor: '#52c41a' }}
              showZero
              overflowCount={999}
            />
          </Space>
          <Space>
            <Search
              placeholder={t('sessions.searchPlaceholder')}
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
            <Select
              style={{ width: 200 }}
              placeholder={t('common.allEvents')}
              allowClear
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('sessions.new_session')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredSessions}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              t('sessions.paginationTotal', { from: range[0], to: range[1], count: total }),
            showQuickJumper: true,
          }}
        />
      </Space>

      <Modal
        title={editingSession ? t('sessions.edit_session') : t('sessions.new_session')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSpeakers([]); // Clear speakers when modal closes
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="eventId"
            label={t('common.event')}
            rules={[{ required: true, message: t('common.selectEventRequired') }]}
          >
            <Select placeholder={t('common.selectEvent')} onChange={handleEventChange}>
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label={t('common.title')}
            rules={[{ required: true, message: t('sessions.titleRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="type"
            label={t('sessions.session_type')}
            rules={[{ required: true, message: t('sessions.typeRequired') }]}
          >
            <Select>
              <Option value="PRESENTATION">{t('sessions.types.presentation')}</Option>
              <Option value="WORKSHOP">{t('sessions.types.workshop')}</Option>
              <Option value="PANEL">{t('sessions.types.panel')}</Option>
              <Option value="NETWORKING">{t('sessions.types.networking')}</Option>
              <Option value="BREAK">{t('sessions.types.break')}</Option>
              <Option value="KEYNOTE">{t('sessions.types.keynote')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="datetime"
            label={t('common.time')}
            rules={[{ required: true, message: t('sessions.timeRequired') }]}
          >
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="location" label={t('sessions.location')}>
            <Input placeholder={t('sessions.locationPlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item dependencies={['eventId']} noStyle>
            {({ getFieldValue }) => (
              <Form.Item name="speakerIds" label={t('sessions.speakers')}>
                <Select
                  mode="multiple"
                  placeholder={t('sessions.select_speakers')}
                  disabled={!getFieldValue('eventId')}
                >
                  {speakers.map((speaker) => (
                    <Option key={speaker.id} value={speaker.id}>
                      {speaker.name} {speaker.title ? `- ${speaker.title}` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingSession ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('sessions.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Sessions;
